import { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    AGGREGATE_NAMES,
    EVENT_CHAIN_STATUS,
    LOAN_REQUEST_COMMANDS,
    LOAN_REQUEST_RESOLVERS,
    type TEAM_CATEGORY,
} from 'cpool-constants';
import { schemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { toastIKProcessError } from './toastIKProcessError';
import { APICommunicator } from '~/api/APICommunicator';
import { useChainAwareInvalidation } from '~/hooks/useChainAwareInvalidation';
import { buildQueryString, fetcher } from '~/queryUtils/useQuery';
import { queryKeys } from '~/utils/queryKeys';

const leadQueryKeys = (loanRequestId: string) => [
    queryKeys.get(loanRequestId),
    queryKeys.getSummary(),
    [AGGREGATE_NAMES.LOAN_REQUEST, 'getCounts'],
];

// the saga assigns nobody if the team emptied after the command, the chain still completes then
const isIKStepStillOpen = async (loanRequestId: string, category: TEAM_CATEGORY) => {
    const schema = schemaLoanRequestResolver_Get.nullable();
    const queryUrl = `/query/${AGGREGATE_NAMES.LOAN_REQUEST}/${LOAN_REQUEST_RESOLVERS.get}`;
    try {
        const lead = await fetcher<typeof schema>({
            meta: { queryUrl: queryUrl + buildQueryString({ id: loanRequestId }), schema },
        });
        return lead?.nextIKCategory === category;
    } catch {
        // e.g. the starter lost access because the lead moved on, so the step was taken
        return false;
    }
};

export const useStartIKProcessMutation = (loanRequestId: string) => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const invalidateOnChainComplete = useChainAwareInvalidation();
    const [isAwaitingAssignment, setIsAwaitingAssignment] = useState(false);

    const mutation = useMutation({
        // the category only feeds the toasts, the backend derives the step itself
        mutationFn: (_category: TEAM_CATEGORY) =>
            APICommunicator.sendCommand({
                aggregateId: loanRequestId,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.startIKProcess,
                payload: {},
            }),
        onError: async (error, category) => {
            toastIKProcessError({ category, error, t });
            await Promise.all(
                leadQueryKeys(loanRequestId).map((queryKey) => queryClient.invalidateQueries({ queryKey })),
            );
        },
        onSuccess: ({ id: correlationId }, category) => {
            const team = t(`TEAMS.CATEGORIES.${category}`);
            toast.success(t('LEADS.IK_PROCESS.START_SUCCESS', { team }));
            setIsAwaitingAssignment(true);
            // the saga assigns the team member, so refetch once its chain is done
            invalidateOnChainComplete({
                correlationId,
                onComplete: async (status) => {
                    if (status === EVENT_CHAIN_STATUS.TIMEOUT)
                        toast.warning(t('LEADS.IK_PROCESS.ASSIGNMENT_TIMEOUT', { team }));
                    if (status === EVENT_CHAIN_STATUS.COMPLETED && (await isIKStepStillOpen(loanRequestId, category)))
                        toast.warning(t('LEADS.IK_PROCESS.ASSIGNMENT_FAILED', { team }));
                    setIsAwaitingAssignment(false);
                },
                queryKeys: [...leadQueryKeys(loanRequestId), queryKeys.getAclPermissions({ loanRequestId })],
            });
        },
    });

    return { ...mutation, isAwaitingAssignment };
};
